import { createContext, useCallback, useContext } from "react";
import { useLocation, useNavigate } from "react-router";
import type { Location, NavigateOptions, To } from "react-router";
import { useDashboardContext } from "../components/dashboard/Dashboard.shared";

export type ParentNavigation = Pick<
  Location,
  "hash" | "key" | "pathname" | "search"
> & {
  historyIndex?: number;
};

type ParentNavigationState = {
  parentNavigation: ParentNavigation;
};

const isParentNavigationState = (
  state: unknown,
): state is ParentNavigationState => {
  if (!state || typeof state !== "object" || !("parentNavigation" in state)) {
    return false;
  }

  const parentNavigation = state.parentNavigation;
  return (
    !!parentNavigation &&
    typeof parentNavigation === "object" &&
    "key" in parentNavigation &&
    typeof parentNavigation.key === "string" &&
    "pathname" in parentNavigation &&
    typeof parentNavigation.pathname === "string" &&
    "search" in parentNavigation &&
    typeof parentNavigation.search === "string" &&
    "hash" in parentNavigation &&
    typeof parentNavigation.hash === "string" &&
    (!("historyIndex" in parentNavigation) ||
      parentNavigation.historyIndex === undefined ||
      typeof parentNavigation.historyIndex === "number")
  );
};

const isStateRecord = (state: unknown): state is Record<string, unknown> =>
  !!state && typeof state === "object" && !Array.isArray(state);

const getHistoryIndex = (): number | undefined => {
  if (typeof window === "undefined" || !isStateRecord(window.history.state)) {
    return;
  }

  const historyIndex = window.history.state.idx;
  return typeof historyIndex === "number" ? historyIndex : undefined;
};

export const getParentNavigation = (state: unknown): ParentNavigation | null =>
  isParentNavigationState(state) ? state.parentNavigation : null;

export const createParentNavigationState = (
  location: Location,
  state?: unknown,
): ParentNavigationState & Record<string, unknown> => ({
  ...(isStateRecord(state) ? state : {}),
  parentNavigation: {
    key: location.key,
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
    historyIndex: getHistoryIndex(),
  },
});

export const ParentNavigationContext = createContext<
  ParentNavigation | null | undefined
>(undefined);

type NavigateToChildOptions = Omit<NavigateOptions, "replace" | "state"> & {
  state?: unknown;
};

export const useNavigateToChild = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isTwoPanel } = useDashboardContext();

  return useCallback(
    (to: To, options?: NavigateToChildOptions) => {
      navigate(to, {
        ...options,
        replace: false,
        state: isTwoPanel
          ? options?.state
          : createParentNavigationState(location, options?.state),
      });
    },
    [isTwoPanel, location, navigate],
  );
};

const useResolvedParentNavigation = (): ParentNavigation | null => {
  const location = useLocation();
  const appParentNavigation = useContext(ParentNavigationContext);
  const { isTwoPanel } = useDashboardContext();

  if (isTwoPanel) return null;

  return appParentNavigation === undefined
    ? getParentNavigation(location.state)
    : appParentNavigation;
};

type UseParentNavigationOptions = { fallback: To } | { fallback: () => void };

export const useHasParentNavigation = (): boolean => {
  return !!useResolvedParentNavigation();
};

export const useParentNavigation = (options: UseParentNavigationOptions) => {
  const navigate = useNavigate();
  const parentNavigation = useResolvedParentNavigation();
  const { fallback } = options;

  return useCallback(() => {
    if (parentNavigation) {
      const currentHistoryIndex = getHistoryIndex();
      const historyDelta =
        currentHistoryIndex !== undefined &&
        parentNavigation.historyIndex !== undefined
          ? currentHistoryIndex - parentNavigation.historyIndex
          : 1;

      navigate(historyDelta > 0 ? -historyDelta : -1);
      return;
    }

    if (typeof fallback === "function") {
      fallback();
      return;
    }

    navigate(fallback, { replace: true });
  }, [fallback, navigate, parentNavigation]);
};
